import { darken, lighten } from '@mui/material/styles'

import { MALU_THEME } from '../branding/defaults'
import { getFontCssStack } from '../branding/fonts'
import type { EffectiveBranding } from '../branding/types'
import { tabletUp, laptopUp, desktopUp } from './breakpoints'

const maluSecondary = MALU_THEME.secondary
const maluSecondaryLight = MALU_THEME.secondaryLight
const success = MALU_THEME.success
const error = MALU_THEME.error
const contrastText = MALU_THEME.contrastText
const linkLight = MALU_THEME.linkLight
const linkDark = MALU_THEME.linkDark
const text = MALU_THEME.text
const textDarkBackground = MALU_THEME.textDarkBackground
const greyOut = MALU_THEME.greyOut
const lightBackground = MALU_THEME.lightBackground

export const buildThemePalette = (branding: EffectiveBranding) => {
  const primary = branding.accentColor
  const background = branding.pageBackground

  return {
    primary: {
      main: primary,
      light: lighten(primary, 0.2),
      dark: darken(primary, 0.2),
      contrastText,
    },
    secondary: {
      main: maluSecondary,
      light: maluSecondaryLight,
      dark: darken(maluSecondary, 0.2),
      contrastText,
    },
    success: {
      main: success,
      contrastText,
    },
    error: {
      main: error,
      contrastText,
    },
    background: {
      default: background,
      light: lightBackground,
      secondary: branding.pageBackground,
    },
    text: {
      primary: text,
      secondary: text,
      greyOut,
      darkBackground: textDarkBackground,
      contrastText,
    },
    custom: {
      linkLight: {
        main: linkLight,
        light: lighten(primary, 0.2),
        dark: darken(primary, 0.2),
        contrastText,
      },
      linkDark: {
        main: linkDark,
        light: lighten(linkDark, 0.1),
        dark: darken(linkDark, 0.1),
        contrastText,
      },
    },
    branding: {
      enabled: branding.enabled,
      hideMaluBranding: branding.hideMaluBranding,
      accentColor: branding.accentColor,
      headerBackground: branding.headerBackground,
      footerBackground: branding.footerBackground,
      pageBackground: branding.pageBackground,
      button: branding.button,
      footerText: branding.footerText,
      footerTextHover: branding.footerTextHover,
      buttonText: branding.buttonText,
      timerChipBackground: branding.timerChipBackground,
      timerChipText: branding.timerChipText,
      highlightChipBackground: branding.highlightChipBackground,
      highlightChipText: branding.highlightChipText,
    },
  }
}

export const buildThemeTypography = (branding: EffectiveBranding) => ({
  fontSize: 21,
  fontFamily: getFontCssStack(branding.fontFamily),
  body1: {
    fontSize: '1rem',
    lineHeight: 1.5,
  },
  h1: {
    fontSize: '2.5rem',
    [tabletUp]: { fontSize: '3rem' },
    [laptopUp]: { fontSize: '3.5rem' },
    [desktopUp]: { fontSize: '4rem' },
  },
  h2: {
    fontSize: '2rem',
    [tabletUp]: { fontSize: '2.5rem' },
    [laptopUp]: { fontSize: '3rem' },
    [desktopUp]: { fontSize: '3.5rem' },
  },
  h3: {
    fontSize: '1.75rem',
    [tabletUp]: { fontSize: '2rem' },
    [laptopUp]: { fontSize: '2.5rem' },
    [desktopUp]: { fontSize: '3rem' },
  },
  h4: {
    fontSize: '1.5rem',
    [tabletUp]: { fontSize: '1.7rem' },
    [laptopUp]: { fontSize: '1.75rem' },
    [desktopUp]: { fontSize: '2rem' },
  },
  h5: { fontWeight: 600 },
  button: { textTransform: 'none' as const },
  fontWeightLight: 300,
  fontWeightRegular: 400,
  fontWeightMedium: 500,
  fontWeightBold: 700,
})
